import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { 
  ArrowRight, 
  ArrowLeft, 
  CheckCircle2,
  AlertCircle,
  Loader2
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Progress } from "../components/ui/progress";
import { Checkbox } from "../components/ui/checkbox";
import { Slider } from "../components/ui/slider";
import { toast } from "sonner";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function TestPage() {
  const navigate = useNavigate();
  const [preguntas, setPreguntas] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [currentSection, setCurrentSection] = useState(0);
  const [currentQuestion, setCurrentQuestion] = useState(0);
  const [respuestas, setRespuestas] = useState({});
  const [showIntro, setShowIntro] = useState(true);

  const sections = preguntas ? [
    { key: "seccion_a", data: preguntas.seccion_a },
    { key: "seccion_b", data: preguntas.seccion_b },
    { key: "seccion_c", data: preguntas.seccion_c }
  ] : [];

  useEffect(() => {
    const fetchPreguntas = async () => {
      try {
        const response = await axios.get(`${API}/preguntas`);
        setPreguntas(response.data);
      } catch (error) {
        console.error("Error fetching preguntas:", error);
        toast.error("Error al cargar las preguntas");
      } finally {
        setLoading(false);
      }
    };
    fetchPreguntas();
  }, []);

  const currentSectionData = sections[currentSection]?.data;
  const currentQuestionData = currentSectionData?.preguntas[currentQuestion];
  
  const totalQuestions = sections.reduce((acc, s) => acc + (s.data?.preguntas?.length || 0), 0);
  const answeredQuestions = Object.keys(respuestas).filter(k => 
    respuestas[k]?.opciones_seleccionadas?.length > 0
  ).length;
  const progressPercent = (answeredQuestions / totalQuestions) * 100;

  const globalQuestionIndex = sections.slice(0, currentSection)
    .reduce((acc, s) => acc + (s.data?.preguntas?.length || 0), 0) + currentQuestion + 1;

  const handleOptionToggle = (questionId, optionIndex) => {
    setRespuestas(prev => {
      const current = prev[questionId] || { pregunta_id: questionId, opciones_seleccionadas: [], intensidades: {} };
      const selected = [...current.opciones_seleccionadas];
      const idx = selected.indexOf(optionIndex);
      
      if (idx > -1) {
        selected.splice(idx, 1);
        const newIntensidades = { ...current.intensidades };
        delete newIntensidades[optionIndex];
        return { ...prev, [questionId]: { ...current, opciones_seleccionadas: selected, intensidades: newIntensidades } };
      } else {
        selected.push(optionIndex);
        return { ...prev, [questionId]: { ...current, opciones_seleccionadas: selected, intensidades: { ...current.intensidades, [optionIndex]: 3 } } };
      }
    });
  };

  const handleIntensityChange = (questionId, optionIndex, value) => {
    setRespuestas(prev => {
      const current = prev[questionId] || { pregunta_id: questionId, opciones_seleccionadas: [], intensidades: {} };
      return {
        ...prev,
        [questionId]: {
          ...current,
          intensidades: { ...current.intensidades, [optionIndex]: value[0] }
        }
      };
    });
  };

  const canProceed = () => {
    if (!currentQuestionData) return false;
    const questionId = currentQuestionData.id;
    const respuesta = respuestas[questionId];
    return respuesta?.opciones_seleccionadas?.length > 0;
  };

  const handleNext = () => {
    if (!canProceed()) {
      toast.error("Por favor selecciona al menos una opción");
      return;
    }

    if (currentQuestion < currentSectionData.preguntas.length - 1) {
      setCurrentQuestion(prev => prev + 1);
    } else if (currentSection < sections.length - 1) {
      setCurrentSection(prev => prev + 1);
      setCurrentQuestion(0);
    } else {
      handleSubmit();
    }
  };

  const handlePrevious = () => {
    if (currentQuestion > 0) {
      setCurrentQuestion(prev => prev - 1);
    } else if (currentSection > 0) {
      setCurrentSection(prev => prev - 1);
      const prevSection = sections[currentSection - 1];
      setCurrentQuestion(prevSection.data.preguntas.length - 1);
    }
  };

  const handleSubmit = async () => {
    setSubmitting(true);
    try {
      const respuestasArray = Object.values(respuestas).map(r => ({
        pregunta_id: r.pregunta_id,
        opciones_seleccionadas: r.opciones_seleccionadas,
        intensidades: r.intensidades || {},
        texto_libre: r.texto_libre || null
      }));

      const response = await axios.post(`${API}/evaluar-arquetipos`, {
        respuestas: respuestasArray
      });

      const resultadoId = response.data.id;
      localStorage.setItem(`resultado_${resultadoId}`, JSON.stringify(response.data));
      
      navigate(`/resultados/${resultadoId}`);
    } catch (error) {
      console.error("Error submitting test:", error);
      toast.error("Error al procesar el test. Por favor intenta de nuevo.");
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="animate-spin text-primary" size={48} />
      </div>
    );
  }

  if (showIntro) {
    return (
      <div className="min-h-screen py-12 px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="max-w-2xl mx-auto text-center"
        >
          <div className="glass-card rounded-2xl p-8 sm:p-12">
            <h1 className="font-heading text-3xl sm:text-4xl text-white mb-6">
              Antes de Comenzar
            </h1>
            
            <div className="text-left space-y-4 mb-8">
              <div className="flex items-start gap-3">
                <CheckCircle2 className="text-secondary shrink-0 mt-1" size={20} />
                <p className="font-body text-primary-200">
                  <strong className="text-white">30 preguntas</strong> divididas en 3 secciones
                </p>
              </div>
              <div className="flex items-start gap-3">
                <CheckCircle2 className="text-secondary shrink-0 mt-1" size={20} />
                <p className="font-body text-primary-200">
                  Duración aproximada: <strong className="text-white">15-20 minutos</strong>
                </p>
              </div>
              <div className="flex items-start gap-3">
                <CheckCircle2 className="text-secondary shrink-0 mt-1" size={20} />
                <p className="font-body text-primary-200">
                  Puedes seleccionar <strong className="text-white">múltiples opciones</strong> si aplican
                </p>
              </div>
              <div className="flex items-start gap-3">
                <CheckCircle2 className="text-secondary shrink-0 mt-1" size={20} />
                <p className="font-body text-primary-200">
                  Si seleccionas varias, indica la <strong className="text-white">intensidad</strong> de cada una
                </p>
              </div>
              <div className="flex items-start gap-3">
                <AlertCircle className="text-accent-glow shrink-0 mt-1" size={20} />
                <p className="font-body text-primary-200">
                  Responde con <strong className="text-white">honestidad</strong>, no hay respuestas correctas o incorrectas
                </p>
              </div>
            </div>

            <div className="bg-primary-950/50 rounded-lg p-4 mb-8 text-left">
              <p className="font-body text-sm text-primary-300 italic">
                "El primer paso hacia la transformación es la consciencia. 
                Observa tus respuestas con curiosidad, no con juicio."
              </p>
            </div>

            <Button 
              onClick={() => setShowIntro(false)}
              size="lg"
              className="bg-primary hover:bg-primary/90 text-white rounded-full px-10 py-6 text-lg shadow-glow-primary transition-all hover:scale-105"
              data-testid="start-test-button"
            >
              Comenzar el Test
              <ArrowRight className="ml-2" size={20} />
            </Button>
          </div>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="min-h-screen py-8 px-4 sm:px-6 lg:px-8">
      <div className="max-w-3xl mx-auto">
        {/* Progress Header */}
        <div className="glass-card rounded-xl p-4 mb-6 sticky top-20 z-20">
          <div className="flex items-center justify-between mb-2">
            <span className="font-body text-sm text-primary-200">
              Sección {currentSection + 1} de 3: {currentSectionData?.titulo}
            </span>
            <span className="font-body text-sm text-secondary">
              {globalQuestionIndex} / {totalQuestions}
            </span>
          </div>
          <Progress value={progressPercent} className="h-2 bg-primary-950" />
        </div>

        {/* Question Card */}
        <AnimatePresence mode="wait">
          <motion.div
            key={`${currentSection}-${currentQuestion}`}
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -20 }}
            transition={{ duration: 0.3 }}
            className="glass-card rounded-2xl p-6 sm:p-8"
          >
            {/* Section Description (first question of each section) */}
            {currentQuestion === 0 && (
              <div className="bg-secondary/10 rounded-lg p-4 mb-6">
                <p className="font-body text-sm text-secondary">
                  {currentSectionData?.descripcion}
                </p>
              </div>
            )}

            {/* Question */}
            <h2 className="font-heading text-xl sm:text-2xl text-white mb-6">
              {currentQuestionData?.texto}
            </h2>

            {/* Options */}
            <div className="space-y-3">
              {currentQuestionData?.opciones.map((opcion, index) => {
                const questionId = currentQuestionData.id;
                const isSelected = respuestas[questionId]?.opciones_seleccionadas?.includes(index);
                const intensity = respuestas[questionId]?.intensidades?.[index] || 3;

                return (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: index * 0.05 }}
                    className={`rounded-xl border transition-all ${
                      isSelected 
                        ? "border-secondary bg-secondary/10" 
                        : "border-white/10 bg-white/5 hover:border-white/20"
                    }`}
                  >
                    <label 
                      className="flex items-start gap-3 p-4 cursor-pointer"
                      data-testid={`option-${questionId}-${index}`}
                    >
                      <Checkbox
                        checked={isSelected}
                        onCheckedChange={() => handleOptionToggle(questionId, index)}
                        className="mt-0.5 border-primary-300 data-[state=checked]:bg-secondary data-[state=checked]:border-secondary"
                      />
                      <span className="font-body text-primary-100 flex-1">
                        {opcion.texto}
                      </span>
                    </label>

                    {/* Intensity Slider */}
                    <AnimatePresence>
                      {isSelected && (
                        <motion.div
                          initial={{ opacity: 0, height: 0 }}
                          animate={{ opacity: 1, height: "auto" }}
                          exit={{ opacity: 0, height: 0 }}
                          className="px-4 pb-4"
                        >
                          <div className="flex items-center gap-4">
                            <span className="font-body text-xs text-primary-300 w-16">
                              Intensidad:
                            </span>
                            <Slider
                              value={[intensity]}
                              onValueChange={(value) => handleIntensityChange(questionId, index, value)}
                              min={1}
                              max={5}
                              step={1}
                              className="flex-1"
                            />
                            <span className="font-body text-sm text-secondary w-8 text-center">
                              {intensity}
                            </span>
                          </div>
                          <div className="flex justify-between mt-1 px-16">
                            <span className="text-xs text-primary-400">Leve</span>
                            <span className="text-xs text-primary-400">Muy fuerte</span>
                          </div>
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </motion.div>
                );
              })}
            </div>

            {/* Navigation */}
            <div className="flex items-center justify-between mt-8 pt-6 border-t border-white/10">
              <Button
                variant="outline"
                onClick={handlePrevious}
                disabled={currentSection === 0 && currentQuestion === 0}
                className="border-white/20 text-primary-200 hover:bg-white/5"
                data-testid="prev-button"
              >
                <ArrowLeft className="mr-2" size={16} />
                Anterior
              </Button>

              <Button
                onClick={handleNext}
                disabled={!canProceed() || submitting}
                className="bg-primary hover:bg-primary/90 text-white"
                data-testid="next-button"
              >
                {submitting ? (
                  <>
                    <Loader2 className="mr-2 animate-spin" size={16} />
                    Procesando...
                  </>
                ) : currentSection === sections.length - 1 && currentQuestion === currentSectionData?.preguntas.length - 1 ? (
                  <>
                    Ver Resultados
                    <CheckCircle2 className="ml-2" size={16} />
                  </>
                ) : (
                  <>
                    Siguiente
                    <ArrowRight className="ml-2" size={16} />
                  </>
                )}
              </Button>
            </div>
          </motion.div>
        </AnimatePresence>
      </div>
    </div>
  );
}
